import {useQuery} from '@apollo/client';
import {DatePicker, Form, Spin} from 'antd';
import {UserOutlined, DollarOutlined} from '@ant-design/icons';
import {
  DABA_ACADEMY_SUBSCRIPTION_STATISTICS,
  DABA_ACADEMY_SUBSCRIPTION_MONTHLY_BREAKDOWN,
} from '../../home/SuperAdminOverview/resolvers';
import styles from '../analytics.module.scss';
import {useMemo, useState} from 'react';
import SubmitButton from 'components/Startup/components/SubmitButton';
import moment from 'moment';
import {DabaAcademyMembersTable} from './DabaAcademyMembersTable';
import {AcademyAdminNotice} from './AcademyAdminNotice';

export const DabaAcademyOverviewPage = () => {
  const [form] = Form.useForm();
  const [selectedYear, setSelectedYear] = useState(moment());
  const [appliedFilters, setAppliedFilters] = useState(null);

  const {
    data,
    loading,
    refetch: refetchStats,
  } = useQuery(DABA_ACADEMY_SUBSCRIPTION_STATISTICS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const {data: monthlyData, loading: monthlyLoading} = useQuery(
    DABA_ACADEMY_SUBSCRIPTION_MONTHLY_BREAKDOWN,
    {
      variables: {year: Number(selectedYear.format('YYYY'))},
      fetchPolicy: 'no-cache',
    },
  );

  const onFinish = values => {
    const {startDate, endDate} = values || {};
    const filters = {
      startDate: startDate ? moment(startDate).format() : null,
      endDate: endDate ? moment(endDate).format() : null,
    };
    setAppliedFilters(filters);
    refetchStats({filters});
  };

  const stats = useMemo(() => {
    return data?.getDabaAcademySubscriptionStatistics?.data
      ? data.getDabaAcademySubscriptionStatistics.data
      : {};
  }, [data]);

  const cardData = useMemo(
    () => [
      {
        title: 'Total Revenue (Selected Period)',
        value: `${stats.totalRevenueSelectedPeriod?.symbol || '$'}${Number(
          stats.totalRevenueSelectedPeriod?.totalAmount || 0,
        ).toLocaleString()}`,
        subtitle: stats.totalRevenueSelectedPeriod?.currency || 'USD',
        icon: <DollarOutlined style={{fontSize: '24px', color: '#13c2c2'}} />,
        iconBg: '#e6fffb',
        isValueString: true,
      },
      {
        title: 'Total Revenue (Since Launch)',
        value: `${stats.totalRevenueSinceLaunch?.symbol || '$'}${Number(
          stats.totalRevenueSinceLaunch?.totalAmount || 0,
        ).toLocaleString()}`,
        subtitle: stats.totalRevenueSinceLaunch?.currency || 'USD',
        icon: <DollarOutlined style={{fontSize: '24px', color: '#722ed1'}} />,
        iconBg: '#f9f0ff',
        isValueString: true,
      },
      {
        title: 'Total Academy Members',
        value: stats.totalMembersSinceLaunch || 0,
        subtitle: 'Since launch',
        icon: <UserOutlined style={{fontSize: '24px', color: '#1890ff'}} />,
        iconBg: '#e6f7ff',
      },
    ],
    [stats],
  );

  const monthlyBreakdownData = useMemo(() => {
    const breakdown =
      monthlyData?.getDabaAcademySubscriptionMonthlyBreakdown?.data
        ?.breakdown || [];
    const breakdownMap = breakdown.reduce((acc, item) => {
      if (item.month) {
        acc[item.month.toLowerCase()] = item.totalCount;
      }
      return acc;
    }, {});

    return moment.months().map(month => ({
      month,
      count: breakdownMap[month.toLowerCase()] || 0,
    }));
  }, [monthlyData]);

  if (!data && loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <AcademyAdminNotice />
      <div className={styles.filterSection}>
        <Form layout="inline" onFinish={onFinish} form={form}>
          <Form.Item label="Start Date" name="startDate">
            <DatePicker showTime />
          </Form.Item>
          <Form.Item label="End Date" name="endDate">
            <DatePicker showTime />
          </Form.Item>
          <SubmitButton label="Apply" smallWidth />
        </Form>
      </div>
      <div
        style={{
          marginBottom: '16px',
          fontSize: '14px',
          color: '#595959',
          padding: '12px 16px',
          backgroundColor: '#e6f7ff',
          borderRadius: '8px',
          border: '1px solid #91d5ff',
        }}>
        <strong>Note:</strong> Date filters only apply to{' '}
        <strong>Total Revenue (Selected Period)</strong>.<br />
        {appliedFilters?.startDate || appliedFilters?.endDate ? (
          <span>
            Currently showing filtered results from{' '}
            <strong>
              {appliedFilters?.startDate
                ? moment(appliedFilters.startDate).format('MMMM Do, YYYY')
                : moment().startOf('month').format('MMMM Do, YYYY')}
            </strong>{' '}
            to{' '}
            <strong>
              {appliedFilters?.endDate
                ? moment(appliedFilters.endDate).format('MMMM Do, YYYY')
                : moment().format('MMMM Do, YYYY')}
            </strong>
            .
          </span>
        ) : (
          <span>
            By default, showing results from{' '}
            <strong>{moment().startOf('month').format('MMMM Do, YYYY')}</strong>{' '}
            to <strong>{moment().format('MMMM Do, YYYY')}</strong>.
          </span>
        )}
      </div>
      <div className={styles.dabaProGrid}>
        {cardData.map((item, index) => {
          const isAffectedByDate = index === 0; // Only the 2nd card is affected
          const isCardLoading = isAffectedByDate && loading;

          return (
            <Spin spinning={isCardLoading} key={index}>
              <div
                className={`${styles.metricOverviewCard} ${
                  isAffectedByDate ? styles.filteredMetricCard : ''
                }`}>
                <div
                  className={styles.dabaProIconWrapper}
                  style={{backgroundColor: item.iconBg}}>
                  {item.icon}
                </div>
                <div className={styles.dabaProTextWrapper}>
                  <span className={styles.dabaProValue}>
                    {item.isValueString
                      ? item.value
                      : Number(item.value).toLocaleString()}
                  </span>
                  <span className={styles.dabaProTitle}>{item.title}</span>
                  <span className={styles.dabaProSubtitle}>
                    {item.subtitle}
                  </span>
                </div>
              </div>
            </Spin>
          );
        })}
      </div>

      <div className={styles.breakdownSection}>
        <div className={styles.breakdownHeader}>
          <h3 className={styles.sectionTitle}>
            {selectedYear.format('YYYY')} MONTHLY SALES BREAKDOWN
          </h3>
          <div className={styles.yearFilter}>
            <span className={styles.filterLabel}>Filter Breakdown by Year</span>
            <DatePicker
              picker="year"
              allowClear={false}
              value={selectedYear}
              onChange={val => val && setSelectedYear(val)}
              className={styles.yearPicker}
            />
          </div>
        </div>
        {monthlyLoading ? (
          <div className={styles.loading} style={{height: '200px'}}>
            <Spin />
          </div>
        ) : (
          <div className={styles.monthlyBreakdownGrid}>
            {monthlyBreakdownData.map((item, index) => (
              <div key={index} className={styles.monthlyCard}>
                <span className={styles.monthLabel}>{item.month}</span>
                <span className={styles.monthValue}>{item.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <DabaAcademyMembersTable />
    </div>
  );
};
